import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ISurgeItemRef, ISurgeMatchedRule, ISurgePriceCheckResult } from "@/api_framework/api_modals/SurgePricing";
import SimpleBadge from "@/app/components/ui/Badges/SimpleBadge";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useSurgePricingAPI } from "@/app/hooks/api_hooks/surge_pricing/useSurgePricingAPI";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { useRouter } from "@/app/hooks/useRouter";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import { FormRow, PageHeader, SectionCard } from "./components";
import ItemPicker from "./ItemPicker";
import { clinicsInCities, epochToDate, pickItem, pickItems, toISTIso, useSurgeLookups } from "./interface";

interface IPriceCheckForm {
  city_id: string;
  clinic_id: string;
  service_type_id: string;
  visit_date: string;
  visit_h: string;
  visit_m: string;
  persona_tags: string[];
  item_search: string;
  item: ISurgeItemRef[];
}


const tableCellClassName = "text-xs font-medium text-black whitespace-normal py-3 px-4";
const tableHeaderClassName = "text-xs font-semibold text-[#193A6A] py-3 px-4";
const matchColumns: TableColumn<ISurgeMatchedRule>[] = [
  { id: "name", header: "Rule", className: tableCellClassName, headerClassName: tableHeaderClassName, cell: ({ row }) => row.name ?? row.rule_id },
  {
    id: "price",
    header: "Price With Rule",
    className: `${tableCellClassName} font-semibold`,
    headerClassName: tableHeaderClassName,
    cell: ({ row }) => `₹${row.price}`,
  },
];

const PriceTile = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div className="flex flex-col gap-1 rounded-lg border border-gray-100 bg-gray-50/50 px-4 py-3">
    <span className="text-xs font-medium text-gray-500">{label}</span>
    <span className={strong ? "text-lg font-bold text-gray-900" : "text-lg font-semibold text-gray-700"}>{value}</span>
  </div>
);

const SurgePriceCheck = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const arrayConstants = useArrayConstants();
  const hourItems = arrayConstants.overBookingStartTimeItems as ISelectMenuItemData[];
  const minuteItems = arrayConstants.overBookingStartTimeMinuteItems as ISelectMenuItemData[];
  const { priceCheck, isLoading } = useSurgePricingAPI();
  const { cityItems, clinicItems, clinicCity, appointmentTypeItems, personaItems } = useSurgeLookups();
  const [result, setResult] = useState<ISurgePriceCheckResult | null>(null);

  const formHook = useForm<IPriceCheckForm>({
    mode: "onChange",
    defaultValues: {
      visit_date: epochToDate(Date.now() / 1000),
      visit_h: "11",
      visit_m: "00",
      persona_tags: [],
      item: [],
    },
  });
  const { register, setValue, watch, formState } = formHook;
  const errors = formState.errors;

  const onSubmit = async (form: IPriceCheckForm) => {
    const [item] = form.item;
    if (!item) return formHook.setError("item_search", { type: "required" });
    setResult(
      await priceCheck({
        clinic_id: form.clinic_id,
        service_type_id: form.service_type_id,
        slot_start: toISTIso(form.visit_date, form.visit_h, form.visit_m),
        item: { item_id: item.item_id, item_type: item.item_type },
        persona_tags: form.persona_tags,
      }),
    );
  };

  const timeSelect = (name: "visit_h" | "visit_m", items: ISelectMenuItemData[], placeholder: string) => (
    <div className="w-24 flex-shrink-0">
      <NonSearchableSelectMenu
        errorMessages={[{ message: "", type: "required" }]}
        onSelectItem={(item) => item && setValue(name, item.title)}
        fieldError={errors[name]}
        register={register(name, { required: true })}
        selectItems={items}
        placeholder={placeholder}
        showTooltips={false}
        showTypedErrors={false}
        showDropdownIcon={true}
        defaultSelected={items.find((i) => i.title === watch(name))}
        listBoxClassName="w-full"
        className="text-gray-800"
      />
    </div>
  );

  const cityId = watch("city_id");
  const cityClinics = clinicsInCities(clinicItems, clinicCity, cityId ? [cityId] : []);

  return (
    <div className="flex flex-col gap-3 pb-24">
      <PageHeader title="Surge Price Check" subtitle="See which rule sets the price of an item for a visit." />

      <form className="flex w-full flex-col gap-3" onSubmit={formHook.handleSubmit(onSubmit)}>
        <SectionCard title="Visit" description="Pick the visit you want to price.">
          <FormRow label="City" required>
            <SearchableSelectMenu
              errorMessages={[{ type: "required", message: "City is required" }]}
              onSelectItem={(item) => {
                if (!item || item.id === cityId) return;
                setValue("city_id", item.id);
                setValue("clinic_id", "");
                setValue("item", []);
                setResult(null);
              }}
              fieldError={errors.city_id}
              register={register("city_id", { required: true })}
              selectItems={cityItems}
              placeholder="Select City"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={pickItem(cityItems, cityId)}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </FormRow>
          <FormRow label="Clinic" required>
            <SearchableSelectMenu
              key={cityId || "no-city"}
              errorMessages={[{ type: "required", message: "Clinic is required" }]}
              onSelectItem={(item) => {
                if (!item || item.id === watch("clinic_id")) return;
                setValue("clinic_id", item.id);
                setValue("item", []);
              }}
              fieldError={errors.clinic_id}
              register={register("clinic_id", { required: true })}
              selectItems={cityClinics}
              placeholder={cityId ? "Select Clinic" : "Select a city first"}
              disabled={!cityId}
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={pickItem(cityClinics, watch("clinic_id"))}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </FormRow>
          <FormRow label="Appointment Type" required>
            <SearchableSelectMenu
              errorMessages={[{ type: "required", message: "Appointment Type is required" }]}
              onSelectItem={(item) => item && setValue("service_type_id", item.id)}
              fieldError={errors.service_type_id}
              register={register("service_type_id", { required: true })}
              selectItems={appointmentTypeItems}
              placeholder="Select Appointment Type"
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              defaultSelected={pickItem(appointmentTypeItems, watch("service_type_id"))}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </FormRow>
          <FormRow label="Visit Date & Time" required>
            <div className="flex flex-wrap items-center gap-3 w-full">
              <div className="w-40 flex-shrink-0">
                <PWLCalendarComponent
                  disableDialogScrollLock
                  showFutureYears
                  errorMessages={[{ message: "Date is required", type: "required" }]}
                  register={register("visit_date", { required: true })}
                  fieldError={errors.visit_date}
                  onSelectDate={(date) => setValue("visit_date", format(date, "yyyy-MM-dd"))}
                  defaultSelectedDate={watch("visit_date")}
                  className="w-full"
                />
              </div>
              <div className="flex items-center gap-2">
                {timeSelect("visit_h", hourItems, "HH")}
                {timeSelect("visit_m", minuteItems, "MM")}
              </div>
            </div>
          </FormRow>
          <FormRow label="Customer Personas" hint="Leave empty to price for any customer.">
            <SearchableMultiSelectMenu
              onSelectItem={(items) => setValue("persona_tags", (items ?? []).map((i) => i.id))}
              errorMessages={[]}
              fieldError={undefined}
              register={register("persona_tags")}
              showTooltips={true}
              showTypedErrors={false}
              showDropdownIcon={true}
              placeholder="All Customers"
              defaultSelected={pickItems(personaItems, watch("persona_tags"))}
              selectItems={personaItems}
              listBoxClassName="w-full"
              className="text-gray-800"
              containerClassName="w-full"
            />
          </FormRow>
          <FormRow label="Item" required>
            <ItemPicker
              clinicId={watch("clinic_id")}
              selected={watch("item")}
              onChange={(list) => {
                setValue("item", list);
                formHook.clearErrors("item_search");
              }}
              register={register("item_search")}
              single
            />
            {errors.item_search && <p className="mt-1 text-xs text-red-600">Item is required</p>}
          </FormRow>
        </SectionCard>

        {result && (
          <SectionCard title={`Result · ${result.name}`} description="Price before coupons and memberships.">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <PriceTile label="Clinic Price" value={`₹${result.clinic_price}`} />
              <PriceTile label="Final Price" value={`₹${result.final_price}`} strong />
              <div className="flex flex-col gap-1 rounded-lg border border-gray-100 bg-gray-50/50 px-4 py-3">
                <span className="text-xs font-medium text-gray-500">Rule Applied</span>
                {result.rule_name ? (
                  <SimpleBadge label={result.rule_name} colorClassName="text-[#0068FF] bg-[#EBF5FF] ring-blue-500/10" />
                ) : (
                  <span className="text-sm font-semibold text-gray-700">None (clinic price)</span>
                )}
              </div>
            </div>
            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold text-gray-700">Matching Rules</p>
              {result.matched_rules.length ? (
                <TableComponent<ISurgeMatchedRule>
                  columns={matchColumns}
                  data={result.matched_rules}
                  getRowId={(row) => row.rule_id}
                  containerClassName="border border-gray-200/80 shadow-sm rounded-2xl mr-0 bg-white"
                  tableClassName="min-w-full divide-y divide-gray-200"
                  wrapCells={true}
                />
              ) : (
                <p className="text-xs text-gray-500">No rule matches this visit.</p>
              )}
            </div>
          </SectionCard>
        )}

        <div className="fixed inset-x-0 bottom-0 z-20 flex w-full items-center justify-end gap-4 border-t-2 bg-white p-4">
          <button
            type="button"
            onClick={() => navigate(getRouteKey("SURGE_PRICING_LIST", "url"))}
            className={`${SECONDARY_BUTTON} flex-1 sm:flex-none`}
          >
            Back
          </button>
          <button type="submit" className={`${PRIMARY_BUTTON} flex-1 sm:flex-none`} disabled={isLoading}>
            Check Price
          </button>
        </div>
      </form>
    </div>
  );
};

export default SurgePriceCheck;
